import type { JSX } from 'react';
import { useMemo } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router';

import { type AdminProject, getMasterProjects, getSubProjects, useGetProjectsQuery } from 'features/projects';

interface TestingCenterLayoutProps {
  masterProjectId: number;
}

export interface TestingCenterOutletContext {
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
}

const PAGE_META_BY_PATH = {
  casos: {
    title: 'Evaluaciones',
    description:
      'Evaluaciones de conversación reportadas desde el chat para convertir en escenarios de testing del agente.',
  },
  escenarios: {
    title: 'Escenarios',
    description: 'Gestiona los escenarios de prueba del agente: globales y propios de la inmobiliaria.',
  },
  runs: {
    title: 'Runs',
    description: 'Historial de corridas de testing del agente y sus resultados.',
  },
} as const;

type TestingCenterPageMeta = (typeof PAGE_META_BY_PATH)[keyof typeof PAGE_META_BY_PATH];

const getPageMeta = (pathname: string): TestingCenterPageMeta => {
  if (pathname.endsWith('/escenarios')) {
    return PAGE_META_BY_PATH.escenarios;
  }

  if (pathname.endsWith('/runs')) {
    return PAGE_META_BY_PATH.runs;
  }

  return PAGE_META_BY_PATH.casos;
};

export const useTestingCenterContext = (): TestingCenterOutletContext => useOutletContext<TestingCenterOutletContext>();

const TestingCenterLayout = ({ masterProjectId }: TestingCenterLayoutProps): JSX.Element => {
  const { pathname } = useLocation();
  const { data: projects = [], isLoading: projectsLoading } = useGetProjectsQuery();
  const pageMeta = getPageMeta(pathname);

  const selectedMaster = useMemo(
    () => getMasterProjects(projects).find((project) => project.id === masterProjectId) ?? null,
    [masterProjectId, projects]
  );

  const subProjectsForContext = useMemo((): AdminProject[] => {
    if (selectedMaster === null) {
      return [];
    }

    return getSubProjects(projects, selectedMaster.id).map((sub) => ({
      id: sub.id,
      name: sub.name,
      masterProjectId: sub.masterProjectId,
      masterProject: sub.masterProject
        ? { id: sub.masterProject.id, name: sub.masterProject.name }
        : { id: selectedMaster.id, name: selectedMaster.name },
    }));
  }, [projects, selectedMaster]);

  const selectionMissingOrInvalid = !projectsLoading && selectedMaster === null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <header className="shrink-0 rounded-xl">
        <h1 className="m-0 text-2xl font-bold text-text-default font-display">{pageMeta.title}</h1>
        <p className="m-0 mt-1 max-w-3xl text-sm text-text-muted font-body">{pageMeta.description}</p>
      </header>

      {selectionMissingOrInvalid ? (
        <div
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border-default
            bg-bg-surface-alt px-6 py-14 text-center"
          role="status"
        >
          <p className="m-0 max-w-md text-sm text-text-muted font-body">
            No se encontró una inmobiliaria válida para el id seleccionado. Elige de nuevo en el selector del menú
            lateral.
          </p>
        </div>
      ) : null}

      {projectsLoading && selectedMaster === null ? (
        <div className="flex flex-1 items-center justify-center rounded-xl border border-border-default bg-bg-surface-alt">
          <p className="m-0 text-sm text-text-muted font-body">Cargando…</p>
        </div>
      ) : null}

      {selectedMaster !== null ? (
        <Outlet context={{ masterProject: selectedMaster, subProjects: subProjectsForContext }} />
      ) : null}
    </div>
  );
};

export default TestingCenterLayout;
